// Context con el "entorno" que necesitan las tool cards del agente para
// abrir flujos reales (p. ej. crear/subir un modelo desde el chat):
//   • userId     — id_auth del usuario (fallback n8n de crear modelo).
//   • companyId  — id_company (guardado del modelo).
//   • shortName / companyName — para prellenar el saludo del mensaje de
//     soporte por WhatsApp (misma fórmula que el resto de la app).
//   • refreshModels — refresca la lista de modelos del padre tras crear uno.
//
// Lo provee InterfazV2 con los valores que ya tiene (userAuthId,
// userCompanyId, shortName, companyName, refreshUserModels).

import { createContext, useContext } from 'react';

export interface AgentEnv {
  userId: string | null;
  companyId: string | null;
  /** Nombre corto del usuario — para el saludo del mensaje de soporte. */
  shortName: string;
  /** Nombre de la marca/empresa — para el saludo del mensaje de soporte. */
  companyName: string;
  refreshModels: () => void;
  /**
   * Plan de la empresa (`company_profiles.plan`) — la carga de productos desde el chat
   * aplica el límite por carga del plan (ADR-2026-09-08-el-chat-trabaja-en-lote). Opcional:
   * los proveedores que no lo resuelven dejan el default de "plan pago".
   */
  plan?: string;
  /** Marca activa (con el desempate canónico de `useRubroMarcaActiva`): a ella se asocian
   *  los productos que se crean desde el chat, como desde Mi marca, y con ella se filtran
   *  las creaciones que muestran las tarjetas. Opcional. */
  brandId?: string | null;
  /**
   * `true` si la cuenta tiene dos o más marcas — el guard de `marcaParaFiltrar`
   * (`utils/filtroDeMarca`) para las tarjetas que listan creaciones.
   *
   * Opcional y `false` por defecto, que es el valor que NO filtra: un proveedor que no lo
   * resuelve deja las listas como estaban en vez de vaciarlas.
   */
  variasMarcas?: boolean;
}

export const AgentEnvContext = createContext<AgentEnv>({
  userId: null,
  companyId: null,
  shortName: '',
  companyName: '',
  refreshModels: () => {},
});

export const useAgentEnv = (): AgentEnv => useContext(AgentEnvContext);
